import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";

const STORAGE_KEY = "transaction-date-filter";

const DateFilterDropdown = ({ sendDateDataFromChild, sendCloseDataFromChild }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [showDropdown, setShowDropdown] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        const customerId = localStorage.getItem("co_customer");
        if (customerId === id) {
            try {
                const parsed = JSON.parse(stored);
                setFromDate(dayjs(parsed.fromDate));
                setToDate(dayjs(parsed.toDate));
                sendDateDataFromChild({ fromDate: dayjs(parsed.fromDate).format("YYYY-MM-DD"), toDate: dayjs(parsed.toDate).format("YYYY-MM-DD") });
            } catch {
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem("co_customer");
            }
        } else {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem("co_customer");
        }
    }, []);

    const toggleDropdown = () => setShowDropdown((prev) => !prev);

    const handleApply = () => {
        const formattedFrom = fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "";
        const formattedTo = toDate ? dayjs(toDate).format("YYYY-MM-DD") : "";

        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ fromDate: formattedFrom, toDate: formattedTo })
        );
        localStorage.setItem('co_customer', id);

        sendDateDataFromChild({ fromDate: formattedFrom, toDate: formattedTo });
        setShowDropdown(false);
    };

    const handleClear = () => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem("co_customer");
        sendCloseDataFromChild();
        setFromDate(null);
        setToDate(null);
        setShowDropdown(false);
    };

    return (
        <div  className="relative inline-block text-left">
            <div
                className="flex w-max text-right items-center cursor-pointer bg-black-300 rounded-lg py-3 sm:py-2 px-4"
                onClick={toggleDropdown}
            >
                <img className="w-4" src="../../assets/icons/table/filter.svg" alt="filter" />
                <div className="hidden sm:block ml-2 text-blue-normal font-medium capitalize">{t('filter')}</div>
            </div>

            {showDropdown && (
                <div className="py-2 bg-white mt-2 absolute right-0 border border-white shadow-around rounded-[10px] w-64 text-left z-50">
                    <div className="px-4 flex justify-between items-center">
                        <h1 className="font-medium capitalize">{t("filter")}</h1>
                        <div onClick={toggleDropdown} className="cursor-pointer text-light-400 text-lg">
                            <img src="../../assets/icons/profile/cancel.svg" alt="cancel" />
                        </div>
                    </div>
                    <div className="mt-2 mb-2 border-t border-background-normal"></div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="px-4 flex flex-col gap-3">
                            <div>
                                <label className="block text-sm font-medium capitalize">{ t('from date')}</label>
                                <DatePicker
                                    value={fromDate}
                                    format="DD-MM-YYYY"
                                    onChange={(newValue) => {
                                        setFromDate(newValue);
                                        if (toDate && dayjs(newValue).isAfter(toDate)) {
                                            setToDate(null);
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            className: "mt-1 block w-full border border-gray-300 rounded p-2 focus:outline-none",
                                        },
                                    }}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium capitalize">{ t('to date')}</label>
                                <DatePicker
                                    value={toDate}
                                    format="DD-MM-YYYY"
                                    minDate={fromDate}
                                    onChange={(newValue) => setToDate(newValue)}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            className: "mt-1 block w-full border border-gray-300 rounded p-2 focus:outline-none",
                                        },
                                    }}
                                />
                            </div>
                            <div className="flex justify-between items-center space-x-2">
                                <button
                                    onClick={handleClear}
                                    className="w-full mt-2 p-2 rounded text-white bg-red-normal cursor-pointer font-semibold capitalize"
                                >
                                    { t('clear')}
                                </button>
                                <button
                                    disabled={!fromDate || !toDate}
                                    onClick={handleApply}
                                    className={`w-full mt-2 p-2 rounded text-white font-semibold capitalize ${
                                        !fromDate || !toDate
                                            ? "bg-gray-400 cursor-not-allowed"
                                            : "bg-blue-normal"
                                    }`}
                                >
                                    { t('apply')}
                                </button>
                            </div>
                        </div>
                    </LocalizationProvider>
                </div>
            )}
        </div>
    );
};

export default DateFilterDropdown;
